<template>
  <div class="mx-auto max-w-screen-fourk overflow-hidden">
    <div ref="elTop" class="flicking-viewport w-full">
      <div class="flicking-camera w-full">
        <div
          v-for="item in mappedItems"
          :key="'top' + item.key"
          class="relative w-full overflow-hidden"
        >
          <ZtLink :to="item.url" draggable="false" class="group/button">
            <MediaImage
              v-bind="item.media"
              responsive-style="hero"
              no-draggable
              class="hero-slider-image w-full"
              image-class="object-cover"
            />
            <div
              class="hero-slider-content absolute bottom-0 left-0 w-full p-4 pb-8 pt-9 tablet:pb-12 desktop:pb-20"
            >
              <div class="container">
                <div class="max-w-[900px]">
                  <h2
                    class="text-size-4 font-bold leading-snug text-white tablet:text-size-3-4 desktop:text-size-2"
                  >
                    <span>{{ item.title }}</span>
                  </h2>
                  <p
                    class="text-balance font-bold leading-tight text-grey-light tablet:text-size-5 desktop:text-size-4"
                  >
                    {{ item.text }}
                  </p>

                  <div
                    class="button is-inverted mt-6 hidden tablet:inline-flex"
                  >
                    {{ item.linkTitle || $texts('learnMore') }}
                  </div>
                </div>
              </div>
            </div>
          </ZtLink>
        </div>
      </div>

      <div class="absolute bottom-0 left-0 z-30 w-full px-section-x">
        <div class="container pb-3 tablet:pb-6 desktop:pb-9">
          <div class="flicking-pagination flex gap-2" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FieldListItem } from '#blokkli/types'
import type {
  MediaImageFragment,
  MediaCantoFragment,
} from '#graphql-operations'
import { notNullish } from '@vueuse/core'
import { buildTeaserProps } from '../Paragraph/Teaser/helper'
import Parallax from '~/helpers/flicking/Parallax'
import { getParagraphOfBundle } from '~/helpers/paragraphs'

const props = defineProps<{
  items: FieldListItem[]
}>()

type HeroSliderItem = {
  key: string
  url: string
  title: string
  text: string
  media: MediaImageFragment | MediaCantoFragment | object
  linkTitle?: string
}

const elTop = ref<HTMLDivElement>()

await useFlicking(elTop, {
  flickingOptions: {
    align: 'prev',
    circular: true,
  },
  useAutoplayPagination: true,
  plugins: () => {
    return new Parallax('img', 1)
  },
})

const mappedItems = computed<HeroSliderItem[]>(() =>
  props.items
    .map((v) => {
      const teaserParagraph = getParagraphOfBundle(v, 'teaser')
      if (!teaserParagraph) {
        return null
      }
      const teaserProps = buildTeaserProps(teaserParagraph)
      if (!teaserProps) {
        return null
      }

      if (
        !teaserProps.media ||
        !teaserProps.title ||
        !teaserProps.url ||
        !teaserProps.text
      ) {
        return null
      }

      return {
        key: v.uuid,
        url: teaserProps.url,
        title: teaserProps.title,
        text: teaserProps.text,
        media: teaserProps.media,
        linkTitle: teaserProps.linkTitle || '',
      }
    })
    .filter(notNullish),
)
</script>

<style lang="postcss">
.hero-slider-content {
  background: linear-gradient(
    rgba(0, 0, 0, 0),
    rgba(0, 0, 0, 0.5) 40%,
    rgba(0, 0, 0, 0.8)
  );
}

.hero-slider-image {
  img {
    --hero-slider-max-width: 1920px;
    --hero-slider-aspect-ratio: calc(9 / 16);
    height: calc(
      var(--small-viewport-height) - var(--page-menu-bottom) -
        var(--viewport-bottom-space)
    );
    min-height: 400px;
    max-height: 100vw;

    @screen tablet {
      --hero-slider-aspect-ratio: calc(16 / 9);
      height: calc(
        var(--small-viewport-height) - var(--page-menu-bottom) -
          var(--viewport-bottom-space) - var(--page-menu-top)
      );
      max-height: calc(
        min(100vw, var(--hero-slider-max-width)) /
          var(--hero-slider-aspect-ratio)
      );
    }
  }
}
</style>
